import { createRouter, createWebHistory } from "vue-router";
import HomePage from "../views/HomePage.vue";
import { useGlobalStore } from "@/stores/global";
import disciplines from "@/data/disciplines";
import { canonicalUrl, peopleSeo } from "@/data/pageSeo";
import { useMetaTags } from "@/composables/useMetaTags";

const routes = [
  {
    path: "/",
    name: "Home",
    component: HomePage,
    meta: {
      pageClass: "page-home",
      title: "IGNIS | Brand Experience Agency in Fulham, London",
      description: "Ignis has been a start-up since 1988, crafting moments that matter for the world’s most exciting brands, that stay with the consumer long after the activation ends.",
    },
  },
  {
    path: "/our-work",
    name: "OurWorkPage",
    component: () => import("../views/OurWorkPage.vue"),
    meta: {
      pageClass: "page-work",
      title: "Our Work | IGNIS",
      description: "Case studies from Ignis London: brand experience work for Pepsi Max, Lipton Ice Tea, Britvic Mixers, Campari, Asahi, Marriott, De’Longhi and JTI.",
    },
  },
  {
    path: "/our-work/asahi",
    name: "AsahiWorkPage",
    component: () => import("../views/work-page/Asahi.vue"),
    meta: { pageClass: "page-case-study" },
  },
  {
    path: "/our-work/britvic-mixers",
    name: "BritvicMixersPage",
    component: () => import("../views/work-page/BritvicMixers.vue"),
    meta: { pageClass: "page-case-study" },
  },
  {
    path: "/our-work/pepsi-max",
    name: "PepsiMaxPage",
    component: () => import("../views/work-page/PepsiMax.vue"),
    meta: { pageClass: "page-case-study" },
  },
  {
    path: "/our-work/lipton-ice-tea",
    name: "LiptonPage",
    component: () => import("../views/work-page/Lipton.vue"),
    meta: { pageClass: "page-case-study" },
  },
  {
    path: "/our-work/jti-human-rights",
    name: "HumanRightsPage",
    component: () => import("../views/work-page/HumanRights.vue"),
    meta: { pageClass: "page-case-study" },
  },
  {
    path: "/our-work/marriott",
    name: "MarriottPage",
    component: () => import("../views/work-page/Marriott.vue"),
    meta: { pageClass: "page-case-study" },
  },
  {
    path: "/our-work/delonghi",
    name: "DeLonghiPage",
    component: () => import("../views/work-page/DeLonghi.vue"),
    meta: { pageClass: "page-case-study" },
  },
  {
    path: "/our-work/campari",
    name: "CampariPage",
    component: () => import("../views/work-page/Campari.vue"),
    meta: { pageClass: "page-case-study" },
  },
  {
    path: "/disciplines",
    redirect: { path: "/", hash: "#disciplines" },
  },
  // One page per discipline, e.g. /experiential. The same view shows that
  // discipline's homepage panel on its own.
  ...disciplines.map((discipline) => ({
    path: discipline.path,
    name: `Discipline-${discipline.key}`,
    component: () => import("../views/DisciplinePage.vue"),
    meta: {
      pageClass: "page-discipline",
      discipline: discipline.key,
      title: discipline.title,
      description: discipline.description,
    },
  })),
  {
    path: "/our-people",
    name: "OurPeoplePage",
    component: () => import("../views/OurPeoplePage.vue"),
    meta: {
      pageClass: "page-people",
      title: peopleSeo([]).title,
      description: peopleSeo([]).description,
    },
  },
  {
    path: "/privacy-policy",
    name: "Privacy Policy",
    component: () => import("../views/PrivacyPage.vue"),
    meta: {
      pageClass: "page-privacy",
      title: "Privacy Policy | IGNIS",
      description: "How Ignis Ltd collects, uses and protects personal data.",
    },
  },
  {
    path: "/cookie-policy",
    name: "Cookie Policy",
    component: () => import("../views/CookiePage.vue"),
    meta: {
      pageClass: "page-cookies",
      title: "Cookie Policy | IGNIS",
      description: "How Ignis Ltd uses cookies on ignis.co.uk.",
    },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("../views/NotFoundPage.vue"),
    meta: { pageClass: "page-not-found", title: "Page not found | IGNIS" },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to) {
    if (to.hash) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const element = document.querySelector(to.hash);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
          resolve();
        }, 300); // Delay to allow DOM to update
      });
    }
    return { top: 0, left: 0, behavior: 'instant' };
  },
});

router.beforeEach((to, from, next) => {
  const globalStore = useGlobalStore();

  if (!globalStore.isDataLoaded) {
    globalStore.fetchData();
  }

  // Set before the URL changes so GA4 page views record this title
  if (to.meta.title) {
    document.title = to.meta.title;
  } else if (from.meta.title) {
    // Leaving the 404 page: don't carry its title onto pages that don't set their own
    document.title = "IGNIS";
  }
  next();
});

// Keep <link rel="canonical"> and the page's meta tags in step with the route.
// Case studies have no route description: they set their own tags when they mount.
router.afterEach((to) => {
  if (to.meta.description) {
    useMetaTags().updateMetaTags({
      title: to.meta.title,
      description: to.meta.description,
      url: canonicalUrl(to.path),
      type: "website",
    });
  }

  let link = document.querySelector('link[rel="canonical"]');

  if (to.name === "NotFound") {
    if (link) link.remove();
    return;
  }

  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", canonicalUrl(to.path));
});

export default router;
